import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Box, IconButton, LinearProgress, Typography, Alert } from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import { getFieldMap, InputsType, InputValidation, inputValidation } from "../../../utils/inputs";
import FileUploader from "../../../Components/Inputs/FileUploader";
import DatePicker from "../../../Components/Inputs/DatePicker";
import Text from "../../../Components/Inputs/Text";
import SocialMedia from "../../../Components/Inputs/SocialMedia";
import FileUploaderMultiple from "../../../Components/Inputs/FileUploaderMultiple";
import ImageSelector from "../../../Components/Inputs/ImageSelector";
import Story from "../../../Components/Inputs/Story";
import Settings from "../../../Components/Inputs/Settings";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
	IAnime,
	IfieldUpdate,
	IfieldVisibility,
	IGallery,
	INVITATION_PRICING_PLANS_ENUM,
	INVITATION_SOCIALS,
	InvitationCoordinates,
	Invitations,
	IStory,
	Social
} from "../../../store/services/invitations/dto/invitations.dto";
import {
	getFullInvitationsState,
	setOpenPricingModal,
	setSelectedField
} from "../../../store/slices/invitations/invitations.slice";
import { convertBase64ToFile } from "../../../utils/convertBase64ToFile";
import {
	useDeleteInvitationGalleryMutation,
	useUpdateInvitationGalleryMutation,
	useUpdateInvitationMutation
} from "../../../store/services/invitations/invitations.service";
import {
	useGetUploadUrlsMutation,
	useOptimizeUploadMutation
	// useUploadFileMutation,
	// useUploadMultipleFilesMutation
} from "../../../store/services/file/file.service";
import { formatISO, isPast } from "date-fns";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { validateHandle } from "../../../utils/validteSocialMediaHandle";
import { forEach, upperFirst } from "lodash";
import { LANG_AR, LTR, RTL } from "../../../Components/Templates/WeddingTemplates/constants";
import CancelSVG from "../../../assets/imgs/cancel.svg";
import { User } from "../../../store/services/auth/dto/auth.dto";
import { prepareImageList } from "../../../utils/prepareImageList";
import { toast } from "react-toastify";
import LocationPicker from "../../../Components/Inputs/LocationPicker";
import { APIProvider } from "@vis.gl/react-google-maps";
import ButtonComponent from "../../../Components/ButtonComponent";
import { PRICING_PLAN_CONFIG } from "../../../constants";
import { UpgradeBtn, WhiteCrown } from "../../../Pages/Invitations/styles";
import axios from "axios";
import { trackEvent } from "../../../utils/analytics";
import { trackMetaEvent } from "../../../utils/metaPixel";
import { trackTikTokEvent } from "../../../utils/tiktokPixel";

type StateType =
	| string
	| number
	| Date
	| boolean
	| null
	| Social[]
	| IStory[]
	| IGallery[]
	| IAnime
	| User
	| IfieldVisibility
	| IfieldUpdate
	| InvitationCoordinates;

const EditPanel = ({
	selectedField,
	postSave,
	isSmallScreen = false
}: {
	selectedField: string;
	postSave?: () => void;
	isSmallScreen?: boolean;
}) => {
	const [invitationId, fieldName, fieldType] = (selectedField || "").split("-");
	const fieldMap = getFieldMap(invitationId);
	const [isSaving, setIsSaving] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const [realProgress, setRealProgress] = useState(0);
	// const [displayProgress, setDisplayProgress] = useState(0);
	// const [fileSize, setFileSize] = useState(0);
	// const { snackbarDisplay } = useSnackBar();
	const dispatch = useAppDispatch();
	const [fieldValueState, setFieldValueState] = useState<StateType>(null);
	const [settingsLoading, setSettingsLoading] = useState<boolean>(false);
	const [fieldTypeState, setFieldTypeState] = useState<string | null>(null);
	const [fieldValidationState, setFieldValidationState] = useState<InputValidation | undefined>(undefined);
	const [deleteImageId, setDeleteImageId] = useState<null | string>(null);
	const { managedInvitation, managedInvitationGallery, invitationPayedData } = useAppSelector(getFullInvitationsState);
	const [callUpdateInvitation, { isLoading: isUpdating }] = useUpdateInvitationMutation();
	const [callUpdateInvitationGallery, { isLoading: isUpdatingGallery }] = useUpdateInvitationGalleryMutation();
	// const [callUploadFile, { isLoading: isUploadingFile }] = useUploadFileMutation();
	// const [callUploadMultipleFiles, { isLoading: isUploadingFiles }] = useUploadMultipleFilesMutation();
	const [callGetUploadUrls] = useGetUploadUrlsMutation();
	const [callOptimizeUpload] = useOptimizeUploadMutation();
	const [callDeleteImage, { isLoading: isDeletingImage }] = useDeleteInvitationGalleryMutation();

	const initFieldValue: StateType = useMemo(() => {
		if (!managedInvitation) {
			return null;
		}
		if (fieldName === "gallery") {
			if (!managedInvitationGallery) {
				return null;
			}
			return [...managedInvitationGallery].sort((a, b) => a.order - b.order);
		}
		if (fieldName === "partners_anime") {
			const { main_image, groom_image, bride_image } = managedInvitation;
			return {
				main_image,
				groom_image,
				bride_image
			} as IAnime;
		}
		return managedInvitation[fieldName as keyof typeof managedInvitation] as StateType;
	}, [managedInvitation, managedInvitationGallery, fieldName]);

	const fieldHidden: boolean = useMemo(() => {
		if (!managedInvitation || !managedInvitation.fieldVisibility) {
			return false;
		}
		const fieldMap = managedInvitation.fieldVisibility[fieldName as keyof IfieldVisibility];
		if (!fieldMap) {
			return false;
		}
		return fieldMap.hidden;
	}, [fieldName, managedInvitation]);

	// useEffect(() => {
	// 	const durationKB = fileSize / 1024;
	// 	const stepTime = 50;
	// 	const totalSteps = durationKB / stepTime;
	//
	// 	const interval = setInterval(() => {
	// 		setDisplayProgress(prev => {
	// 			if (prev < realProgress) {
	// 				const diff = realProgress - prev;
	//
	// 				const step = Math.max(1, Math.ceil(diff / totalSteps));
	//
	// 				return prev + step;
	// 			}
	// 			return prev;
	// 		});
	// 	}, stepTime);
	//
	// 	return () => clearInterval(interval);
	// }, [realProgress, fileSize]);

	const handleVisibilityChange = (status: boolean, fieldName: string) => {
		if (!managedInvitation) {
			return;
		}
		const options: Partial<Invitations> = {
			fieldVisibility: {
				...managedInvitation.fieldVisibility,
				[fieldName]: { hidden: status }
			}
		};
		callUpdateInvitation({ _id: invitationId, ...options }).then(({ data, error }) => {
			if (data) {
				console.log(data);
				postSave && postSave();
			}
			if (error) {
				setIsSaving(false);
				notifyError(error as IError);
			}
		});
	};

	useEffect(() => {
		setFieldValueState(initFieldValue);
		setFieldTypeState(fieldType);
		setFieldValidationState(inputValidation[fieldName as string]);
	}, [initFieldValue]);

	useEffect(() => {
		setIsSaving(isUpdating || isUpdatingGallery);
	}, [isUpdating, isUpdatingGallery]);

	const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFieldValueState(e.target.value);
	};

	const handleSettingsLoadingUpdate = useCallback((status: boolean) => setSettingsLoading(status), []);

	const handleDeleteImage = (deleteImageId: string) => {
		callDeleteImage({ _id: invitationId as string, galleryItemId: deleteImageId }).then(({ data, error }) => {
			if (data) {
				setDeleteImageId(null);
				toast.success("Image has been deleted.", { autoClose: 3000 });
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};
	const handleOpenUpgradeModal = () => {
		if (invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE) {
			trackEvent("upgrade_btn", {
				scope: "Invitations",
				description: "Upgrade Button"
			});
			trackMetaEvent("upgrade_btn", { invitation_id: managedInvitation?._id }, true);
			trackTikTokEvent("upgrade_btn", { invitation_id: managedInvitation?._id });
			return dispatch(setOpenPricingModal(true));
		}
	};
	const handleBackToSettings = () => {
		dispatch(setSelectedField(fieldMap.SETTINGS));
	};

	const getInput = (fieldTypeState: string | null): React.ReactElement | undefined => {
		if (!fieldTypeState) return;
		if (isPast(new Date(managedInvitation?.wedding_date || ""))) {
			return (
				<Box display={"flex"} flexDirection={"column"} alignItems={"center"} gap={6} p={2}>
					<img src={CancelSVG} alt="Edit disabled" width={"300px"} />
					<Typography textAlign={"center"} variant={"h6"}>
						Invitation cannot be modified after the wedding date.
					</Typography>
				</Box>
			);
		}
		console.log(fieldTypeState);
		switch (fieldTypeState) {
			case InputsType.IMAGE: {
				return (
					<FileUploader
						fieldValueState={fieldValueState as string}
						setFieldValueState={setFieldValueState as React.Dispatch<React.SetStateAction<string | IAnime>>}
						isDisabled={isDisabled}
						fieldName={fieldName}
						setIsDisabled={setIsDisabled}
						hidden={fieldHidden}
						visibilityChange={handleVisibilityChange}
						max_image_size={PRICING_PLAN_CONFIG[invitationPayedData.type].image_size}
					/>
				);
			}
			case InputsType.IMAGE_MULTIPLE: {
				return (
					<FileUploaderMultiple
						fieldValueState={fieldValueState as IGallery[]}
						setFieldValueState={setFieldValueState as React.Dispatch<React.SetStateAction<IGallery[]>>}
						deleteImageId={deleteImageId}
						setDeleteImageId={setDeleteImageId}
						onDeleteImage={handleDeleteImage}
						isDeletingImage={isDeletingImage}
						disableDelete={isSaving}
						isDisabled={isDisabled}
						setIsDisabled={setIsDisabled}
						max_image_size={PRICING_PLAN_CONFIG[invitationPayedData.type].image_size}
						max_video_size={PRICING_PLAN_CONFIG[invitationPayedData.type].video_size}
						maxFiles={PRICING_PLAN_CONFIG[invitationPayedData.type].gallery_media_count}
					/>
				);
			}
			case InputsType.IMAGE_SELECT: {
				return (
					<ImageSelector
						fieldValueState={fieldValueState as IAnime}
						setFieldValueState={setFieldValueState as React.Dispatch<React.SetStateAction<IAnime>>}
					/>
				);
			}
			case InputsType.TEXT: {
				return (
					<Text
						value={fieldValueState! as string}
						onChange={handleTextInputChange}
						fieldName={fieldName}
						fieldValidationState={fieldValidationState}
						hidden={fieldHidden}
						visibilityChange={handleVisibilityChange}
						dir={managedInvitation?.language === LANG_AR ? RTL : LTR}
					/>
				);
			}
			case InputsType.TEXT_MULTILINE: {
				return (
					<Text
						value={fieldValueState! as string}
						onChange={handleTextInputChange}
						fieldName={fieldName}
						multiline={true}
						fieldValidationState={fieldValidationState}
						hidden={fieldHidden}
						visibilityChange={handleVisibilityChange}
						dir={managedInvitation?.language === LANG_AR ? RTL : LTR}
					/>
				);
			}
			case InputsType.DATE: {
				return (
					<DatePicker
						fieldValueState={fieldValueState as Date}
						setFieldValueState={setFieldValueState as React.Dispatch<React.SetStateAction<Date | null>>}
					/>
				);
			}
			case InputsType.SOCIAL_MEDIA: {
				return (
					<SocialMedia
						fieldValueState={fieldValueState as Social[]}
						setFieldValueState={setFieldValueState as React.Dispatch<React.SetStateAction<Social[]>>}
					/>
				);
			}
			case InputsType.STORY: {
				return (
					<Story
						fieldValueState={fieldValueState as IStory[]}
						setFieldValueState={setFieldValueState as React.Dispatch<React.SetStateAction<IStory[]>>}
						dir={managedInvitation?.language === LANG_AR ? RTL : LTR}
					/>
				);
			}
			case InputsType.MAP: {
				return (
					<APIProvider apiKey={process.env.REACT_APP_MAPS_API_KEY || ""} libraries={["places", "marker"]}>
						<LocationPicker
							fieldValueState={fieldValueState as InvitationCoordinates}
							setFieldValueState={setFieldValueState as React.Dispatch<React.SetStateAction<InvitationCoordinates>>}
						/>
					</APIProvider>
				);
			}
			default: {
				return <Settings handleLoadingChange={handleSettingsLoadingUpdate} />;
			}
		}
	};

	const handleSave = async () => {
		if (!fieldValueState) return;
		setIsSaving(true);
		switch (fieldType) {
			case InputsType.IMAGE: {
				if (typeof fieldValueState === "object") {
					const { main_image, groom_image, bride_image } = fieldValueState as IAnime;
					return callUpdateInvitation({
						_id: invitationId,
						main_image,
						groom_image,
						bride_image,
						story_image: main_image,
						thumbnail_02: main_image
					}).then(({ data, error }) => {
						if (data) {
							console.log(data);
							postSave && postSave();
						}
						if (error) {
							setIsSaving(false);
							notifyError(error as IError);
						}
					});
				}
				const fileEntries: { file: File; mimeType: string }[] = [];

				const file = await convertBase64ToFile(fieldValueState as string, `${fieldName}-${invitationId}`);

				fileEntries.push({ file, mimeType: file.type });
				const MAX_IMAGE_SIZE = PRICING_PLAN_CONFIG[invitationPayedData.type || 0].image_size;
				try {
					// --- Step 1: get signed upload URLs from backend (tiny JSON request) ---
					const { data: urlData } = await callGetUploadUrls({
						files: fileEntries.map(({ file }) => ({
							fileName: file.name,
							mimeType: file.type,
							size: file.size
						})),
						path: `wedding_invitations/${invitationId}`,
						MAX_IMAGE_SIZE
					});

					if (urlData && !urlData.success) {
						setIsSaving(false);
						return notifyError({ error: urlData.message } as IError);
					}

					// --- Step 2: upload each file directly to GCS (bypasses App Engine) ---
					const totalFiles = fileEntries.length;
					const progressPerFile = new Array(totalFiles).fill(0);
					if (!urlData) {
						return notifyError({ error: "Failed to get signed urls" } as IError);
					}

					const uploadedFiles = await Promise.all(
						urlData.files.map(async (signedFile: any, index: number) => {
							const { file } = fileEntries[index];

							const formData = new FormData();
							// GCS requires signed POST fields to come BEFORE the file
							Object.entries(signedFile.upload_fields).forEach(([key, val]) => {
								formData.append(key, val as string);
							});
							formData.append("file", file);

							await axios.post(signedFile.upload_url, formData, {
								onUploadProgress: e => {
									const total = e.total || file.size;
									const loaded = e.loaded || 0;

									progressPerFile[index] = Math.round((loaded * 100) / total);

									const totalProgress = Math.round(progressPerFile.reduce((a, b) => a + b, 0) / totalFiles);

									console.log(`File ${index} progress:`, progressPerFile[index], "Total:", totalProgress); // remove after debugging

									setRealProgress(totalProgress < 100 ? totalProgress : 99);
								}
							});

							return {
								file_path: signedFile.file_path,
								mimeType: signedFile.mimeType
							};
						})
					);

					// --- Step 3: optimize upload — backend triggers Cloud Run and returns read URLs ---
					const { data: optimizedData } = await callOptimizeUpload({
						files: uploadedFiles,
						toJPEG: "true",
						toWEBP: "false",
						invitationId: invitationId,
						type: "single",
						fieldName: fieldName
					});

					if (!optimizedData?.success) {
						setIsSaving(false);
						return notifyError({ error: optimizedData?.message } as IError);
					}

					if (optimizedData.files.length > 0) {
						callUpdateInvitation({
							_id: invitationId,
							[fieldName]: optimizedData?.files[0].file_path
						}).then(({ data, error }) => {
							if (data) {
								console.log(data);
								setRealProgress(0);
								// setDisplayProgress(0);
								// setFileSize(0);
								postSave && postSave();
							}
							if (error) {
								setIsSaving(false);
								notifyError(error as IError);
							}
						});
					}
				} catch (err) {
					console.error("Upload error:", err);
					setIsSaving(false);
					notifyError({ error: "Upload failed. Please try again." } as IError);
				}

				break;
			}
			case InputsType.IMAGE_MULTIPLE: {
				const fileEntries: { file: File; mimeType: string }[] = [];
				// @ts-ignore
				for (const [, img] of (fieldValueState as IGallery[]).entries()) {
					const _shouldUploadImage = img.image_url.includes("data:image");
					if (_shouldUploadImage) {
						const file = await convertBase64ToFile(
							img.image_url as string,
							`${fieldName}-${invitationId}-${img.order}.jpeg`
						);
						// setFileSize(prev => prev + file.size);
						fileEntries.push({ file, mimeType: file.type });
					}

					const isBlobVideo = img.video_url && img.video_url.startsWith("blob:");
					if (isBlobVideo) {
						const blob = await fetch(img.video_url).then(res => res.blob());
						const ext = blob.type.split("/")[1] || "mp4";
						const file = new File([blob], `${fieldName}-${invitationId}-${img.order}.${ext}`, { type: blob.type });
						// setFileSize(prev => prev + file.size);
						fileEntries.push({ file, mimeType: file.type });
					}
				}
				if (fileEntries.length === 0) {
					// save invitation order
					callUpdateInvitationGallery({ _id: invitationId, gallery: fieldValueState as IGallery[] }).then(
						({ data, error }) => {
							if (data) {
								console.log(data);
								postSave && postSave();
							}
							if (error) {
								setIsSaving(false);
								notifyError(error as IError);
							}
						}
					);
					setIsSaving(false);
					return;
				} else {
					const MAX_IMAGE_SIZE = PRICING_PLAN_CONFIG[invitationPayedData.type || 0].image_size;
					const MAX_VIDEO_SIZE = PRICING_PLAN_CONFIG[invitationPayedData.type || 0].video_size;

					try {
						// --- Step 1: get signed upload URLs from backend (tiny JSON request) ---
						const { data: urlData } = await callGetUploadUrls({
							files: fileEntries.map(({ file }) => ({
								fileName: file.name,
								mimeType: file.type,
								size: file.size
							})),
							path: `wedding_invitations/${invitationId}`,
							MAX_IMAGE_SIZE,
							MAX_VIDEO_SIZE
						});

						if (urlData && !urlData.success) {
							setIsSaving(false);
							return notifyError({ error: urlData.message } as IError);
						}

						// --- Step 2: upload each file directly to GCS (bypasses App Engine) ---
						const totalFiles = fileEntries.length;
						const progressPerFile = new Array(totalFiles).fill(0);
						if (!urlData) {
							return notifyError({ error: "Failed to get signed urls" } as IError);
						}
						const uploadedFiles = await Promise.all(
							urlData.files.map(async (signedFile: any, index: number) => {
								const { file } = fileEntries[index];

								const formData = new FormData();
								// GCS requires signed POST fields to come BEFORE the file
								Object.entries(signedFile.upload_fields).forEach(([key, val]) => {
									formData.append(key, val as string);
								});
								formData.append("file", file);

								await axios.post(signedFile.upload_url, formData, {
									onUploadProgress: e => {
										const total = e.total || file.size;
										const loaded = e.loaded || 0;

										progressPerFile[index] = Math.round((loaded * 100) / total);

										const totalProgress = Math.round(progressPerFile.reduce((a, b) => a + b, 0) / totalFiles);

										console.log(`File ${index} progress:`, progressPerFile[index], "Total:", totalProgress); // remove after debugging

										setRealProgress(totalProgress < 100 ? totalProgress : 99);
									}
								});

								return {
									file_path: signedFile.file_path,
									mimeType: signedFile.mimeType
								};
							})
						);

						// --- Step 3: optimize upload — backend triggers Cloud Run and returns read URLs ---
						const { data: optimizedData } = await callOptimizeUpload({
							files: uploadedFiles,
							toJPEG: "true",
							toWEBP: "false",
							invitationId: invitationId,
							type: "gallery"
						});

						if (!optimizedData?.success) {
							setIsSaving(false);
							return notifyError({ error: optimizedData?.message } as IError);
						}

						// --- Step 4: same as before — update gallery list and save memories ---
						if (optimizedData.files) {
							const updateImageList = prepareImageList(optimizedData.files, fieldValueState as IGallery[]);
							callUpdateInvitationGallery({ _id: invitationId, gallery: updateImageList }).then(({ data, error }) => {
								if (data) {
									console.log(data);
									setRealProgress(0);
									// setDisplayProgress(0);
									// setFileSize(0);
									postSave && postSave();
								}
								if (error) {
									setIsSaving(false);
									notifyError(error as IError);
								}
							});
						}
					} catch (err) {
						console.error("Upload error:", err);
						setIsSaving(false);
						notifyError({ error: "Upload failed. Please try again." } as IError);
					}
				}

				break;
			}
			case InputsType.IMAGE_SELECT: {
				const { main_image, groom_image, bride_image } = fieldValueState as IAnime;
				return callUpdateInvitation({
					_id: invitationId,
					main_image,
					groom_image,
					bride_image
				}).then(({ data, error }) => {
					if (data) {
						console.log(data);
						postSave && postSave();
					}
					if (error) {
						setIsSaving(false);
						notifyError(error as IError);
					}
				});
			}
			case InputsType.TEXT:
			case InputsType.TEXT_MULTILINE: {
				let _fieldValueState = fieldValueState;
				const _callUpdate = async () => {
					return callUpdateInvitation({ _id: invitationId, [fieldName]: _fieldValueState }).then(({ data, error }) => {
						if (data) {
							console.log(data);
							postSave && postSave();
						}
						if (error) {
							setIsSaving(false);
							notifyError(error as IError);
						}
					});
				};

				if (fieldName === "groom_name" || fieldName === "bride_name") {
					_fieldValueState = upperFirst(fieldValueState as string);
				}

				return _callUpdate();
			}
			case InputsType.DATE: {
				return callUpdateInvitation({ _id: invitationId, [fieldName]: formatISO(fieldValueState as Date) }).then(
					({ data, error }) => {
						if (data) {
							console.log(data);
							postSave && postSave();
						}
						if (error) {
							setIsSaving(false);
							notifyError(error as IError);
						}
					}
				);
			}
			case InputsType.SOCIAL_MEDIA: {
				let isValid = true;
				let errorAtIndex = 0;
				let instagramCount = 0;
				let facebookCount = 0;
				let tiktokCount = 0;
				// reset Error
				setFieldValueState(prevState => {
					const _updatedList: Social[] = [...(prevState as Social[])].map(item => ({
						...item,
						hasError: false
					}));
					return _updatedList;
				});

				forEach(fieldValueState as Social[], (item, index) => {
					isValid = validateHandle(item.value, item.social_type);
					if (!isValid) {
						errorAtIndex = index;
						return false;
					}
					switch (item.social_type) {
						case INVITATION_SOCIALS.INSTAGRAM: {
							instagramCount += 1;
							break;
						}
						case INVITATION_SOCIALS.FACEBOOK: {
							facebookCount += 1;
							break;
						}
						default: {
							tiktokCount += 1;
						}
					}
				});
				if (!isValid) {
					setIsSaving(false);
					setFieldValueState(prevState => {
						return [...(prevState as Social[])].map((item, index) => {
							if (errorAtIndex === index) {
								return { ...item, hasError: true };
							}
							return item;
						});
					});
					return notifyError({ error: "Invalid social media link" } as IError);
				}

				setFieldValueState(prevState => {
					let count = 0;
					return [...(prevState as Social[])].map((_item, _index) => {
						if (_item.social_type === INVITATION_SOCIALS.FACEBOOK) {
							count = facebookCount;
						} else if (_item.social_type === INVITATION_SOCIALS.INSTAGRAM) {
							count = instagramCount;
						} else {
							count = tiktokCount;
						}
						return { ..._item, hasError: count > 1 };
					});
				});

				if (instagramCount > 1 || facebookCount > 1 || tiktokCount > 1) {
					setIsSaving(false);
					return notifyError({ error: "Only one Social Account of each app is allowed." } as IError);
				}

				return callUpdateInvitation({
					_id: invitationId,
					[fieldName]: (fieldValueState as Social[]).map(({ social_type, value }) => ({ social_type, value }))
				}).then(({ data, error }) => {
					if (data) {
						setFieldValueState(prevState => {
							const _updatedList: Social[] = [...(prevState as Social[])].map(item => ({
								...item,
								hasError: false
							}));
							return _updatedList;
						});
						postSave && postSave();
					}
					if (error) {
						setIsSaving(false);
						notifyError(error as IError);
					}
				});
			}
			case InputsType.STORY: {
				return callUpdateInvitation({
					_id: invitationId,
					[fieldName]: (fieldValueState as IStory[]).map(({ title, description }) => ({ title, description }))
				}).then(({ data, error }) => {
					if (data) {
						console.log(data);
						postSave && postSave();
					}
					if (error) {
						setIsSaving(false);
						notifyError(error as IError);
					}
				});
			}
			case InputsType.MAP: {
				return callUpdateInvitation({
					_id: invitationId,
					[fieldName]: fieldValueState as InvitationCoordinates
				}).then(({ data, error }) => {
					if (data) {
						console.log(data);
						postSave && postSave();
					}
					if (error) {
						setIsSaving(false);
						notifyError(error as IError);
					}
				});
			}
			default: {
			}
		}
	};

	return (
		<Box display={"flex"} flexDirection={"column"} height={"inherit"}>
			{(isUpdating || isUpdatingGallery || settingsLoading) && (
				<Box className={"linear-progress"} position={"absolute"} top={0} zIndex={3} width={"100%"}>
					<LinearProgress />
				</Box>
			)}
			{!isPast(new Date(managedInvitation?.wedding_date || "")) &&
				invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE && (
					<Box display={"flex"} m={1} justifyContent={"center"} alignItems={"center"}>
						<UpgradeBtn fullWidth variant={"contained"} startIcon={<WhiteCrown />} onClick={handleOpenUpgradeModal}>
							Upgrade
						</UpgradeBtn>
					</Box>
				)}
			<Box
				display={"flex"}
				flexDirection={"column"}
				alignItems={"center"}
				pb={4}
				flexGrow={1}
				overflow={"auto"}
				position={"relative"}
			>
				<Box height={20} />
				{getInput(fieldTypeState)}
			</Box>
			{isSaving && realProgress > 0 ? (
				<Box
					style={{
						position: "sticky",
						bottom: 72,
						width: isSmallScreen ? "100%" : "450px",
						background: isSmallScreen ? "#fff" : "unset",
						zIndex: 1000,
						display: "flex",
						justifyContent: "center",
						alignItems: "flex-start",
						padding: "20px 0"
					}}
				>
					{realProgress < 99 ? (
						<Box display={"flex"} width={"90%"} alignItems={"center"} justifyContent={"space-between"}>
							<LinearProgress variant={"determinate"} value={realProgress} style={{ flexGrow: 1 }} />
							<Typography style={{ paddingLeft: "10px", flexBasis: "35px" }}>{realProgress + "%"}</Typography>
						</Box>
					) : (
						<Typography style={{ fontFamily: "var(--poppins)", fontSize: "16px" }}>Processing...</Typography>
					)}
				</Box>
			) : null}

			{fieldType !== InputsType.SETTINGS && (
				<Box
					p={2}
					bottom={0}
					zIndex={1}
					className={"save-btn-wrapper edit_panel_save_btn_wrapper"}
					style={{ background: isSmallScreen ? "#fff" : "unset" }}
					display={"flex"}
					gap={1}
					alignItems={"center"}
				>
					{fieldType !== InputsType.SETTINGS && (
						<ButtonComponent
							variant={"outlined"}
							color={"primary"}
							fullWidth={true}
							disabled={isSaving || isDisabled}
							loading={isSaving}
							startIcon={<SettingsIcon />}
							onClick={handleBackToSettings}
							text={"Settings"}
						/>
						// <IconButton
						// 	onClick={handleBackToSettings}
						// 	sx={{
						// 		background: theme => theme.palette.secondary.dark,
						// 		color: "#ffffff",
						// 		"&:hover": {
						// 			background: theme => theme.palette.secondary.light
						// 		}
						// 	}}
						// >
						// 	<SettingsIcon />
						// </IconButton>
					)}
					<ButtonComponent
						variant={"contained"}
						color={"primary"}
						fullWidth={true}
						disabled={isSaving || isDisabled || initFieldValue === fieldValueState}
						loading={isSaving}
						onClick={handleSave}
						text={"Save"}
					/>
				</Box>
			)}
		</Box>
	);
};

export default EditPanel;
